import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import RNEventSource from 'react-native-sse';
import { API_URL, API_VERSION } from '../lib/apiUrl';

// Browser ships EventSource as a global; React Native does not. Use the
// react-native-sse polyfill on native so the same hook works across iOS,
// Android, and Web without any per-call branching.
type EventSourceCtor = new (url: string, init?: unknown) => EventSource;
const EventSourceImpl: EventSourceCtor =
  Platform.OS === 'web' &&
  typeof globalThis !== 'undefined' &&
  (globalThis as { EventSource?: unknown }).EventSource
    ? (globalThis as unknown as { EventSource: EventSourceCtor }).EventSource
    : (RNEventSource as unknown as EventSourceCtor);

interface ReconnectConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
}

const DEFAULT_RECONNECT: ReconnectConfig = {
  maxAttempts: 8,
  baseDelay: 3_000,
  maxDelay: 30_000,
};

export interface UseSSEStreamOptions {
  path: string;
  organizationId: string | undefined;
  enabled?: boolean;
  getAccessToken?: () => Promise<string | null>;
  onMessage?: (data: string) => void;
  reconnect?: Partial<ReconnectConfig>;
}

export interface UseSSEStreamReturn {
  isConnected: boolean;
}

export function useSSEStream({
  path,
  organizationId,
  enabled = true,
  getAccessToken,
  onMessage,
  reconnect,
}: UseSSEStreamOptions): UseSSEStreamReturn {
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onMessageRef = useRef(onMessage);
  const getAccessTokenRef = useRef(getAccessToken);
  const connectIdRef = useRef(0);

  onMessageRef.current = onMessage;
  getAccessTokenRef.current = getAccessToken;

  const cfg: ReconnectConfig = {
    ...DEFAULT_RECONNECT,
    ...reconnect,
  };
  const cfgRef = useRef(cfg);
  cfgRef.current = cfg;

  const cleanup = useCallback(() => {
    connectIdRef.current += 1;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const connect = useCallback(async () => {
    if (!organizationId || !enabled) return;

    cleanup();

    const myConnectId = connectIdRef.current;

    if (reconnectAttemptRef.current >= cfgRef.current.maxAttempts) {
      return;
    }

    let tokenParam = '';
    if (getAccessTokenRef.current) {
      try {
        const token = await getAccessTokenRef.current();
        if (token) {
          tokenParam = `&token=${encodeURIComponent(token)}`;
        } else {
          return;
        }
      } catch {
        return;
      }
    }

    if (myConnectId !== connectIdRef.current) return;

    const url = `${API_URL}/api/${API_VERSION}/${path}?organizationId=${organizationId}${tokenParam}`;
    const eventSource = new EventSourceImpl(url);
    eventSourceRef.current = eventSource;

    // Use addEventListener (works for both browser EventSource and the
    // react-native-sse polyfill). Property assignment (onmessage = ...) is
    // a silent no-op on the polyfill, which is why native clients never
    // received any events.
    const handleOpen = () => {
      if (myConnectId !== connectIdRef.current) {
        eventSource.close();
        return;
      }
      setIsConnected(true);
      reconnectAttemptRef.current = 0;
    };

    const handleMessage = (e: { data?: string }) => {
      if (myConnectId !== connectIdRef.current) return;
      if (typeof e?.data === 'string') {
        onMessageRef.current?.(e.data);
      }
    };

    const handleError = () => {
      setIsConnected(false);
      eventSource.close();
      if (eventSourceRef.current === eventSource) {
        eventSourceRef.current = null;
      }

      if (myConnectId !== connectIdRef.current) return;

      reconnectAttemptRef.current += 1;

      if (reconnectAttemptRef.current >= cfgRef.current.maxAttempts) {
        return;
      }

      const delay = Math.min(
        cfgRef.current.baseDelay * 2 ** reconnectAttemptRef.current,
        cfgRef.current.maxDelay,
      );

      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, delay);
    };

    eventSource.addEventListener('open', handleOpen as never);
    eventSource.addEventListener('message', handleMessage as never);
    eventSource.addEventListener('error', handleError as never);
  }, [organizationId, enabled, cleanup, path]);

  useEffect(() => {
    reconnectAttemptRef.current = 0;
    connect();
    return cleanup;
  }, [connect, cleanup]);

  return { isConnected };
}
